<template>
  <div
    class="product-options"
    :class="{
      [$class('loading')]: loading,
      'product-options--gift': product.giftCard,
    }"
  >
    <fieldset
      v-for="(option) of options"
      :key="getOptionKey(option)"
      class="product-options__option"
      :class="{ [$class('hidden')]: !getDisplayOption(option) }"
    >
      <legend
        v-if="displayOptionName"
        class="product-options__option-title text-body-m-bold"
        :for="option.name"
        v-text="getOptionLabel(option)"
      />

      <div
        v-if="product.giftCard"
        class="product-options__values"
      >
        <radio-input
          v-for="(value, index) of option.values"
          :id="getOptionValueKey(value, index, option)"
          :key="getOptionValueKey(value, index, option)"
          v-model="currentOptions[option.index]"
          class="product-options__value"
          class-label="product-options__label--gift text-body-s-bold"
          :disabled="getValueDisabled(option.disabled[value], 'disabled')"
          :label="getOptionValueLabel(value)"
          :name="getOptionKey(option)"
          :value="value"
        />
      </div>

      <div
        v-else-if="section.dropdownOptions?.includes(getOptionHandle(option.name))"
        class="form-group"
      >
        <label
          class="visually-hidden"
          :for="getOptionKey(option)"
          v-text="getOptionLabel(option)"
        />

        <select
          :id="getOptionKey(option)"
          v-model="currentOptions[option.index]"
          class="product-options__select"
        >
          <option
            v-if="
              !section.initialSelect ||
                section.excludedInitialSelectOptions?.includes(getOptionHandle(option.name))
            "
            value=""
            v-text="$string('product.form.select_option')"
          />

          <option
            v-for="(value, index) of option.values"
            :id="getOptionValueKey(value, index, option)"
            :key="getOptionValueKey(value, index, option)"
            :disabled="getValueDisabled(option.disabled[value], 'disabled')"
            :value="value"
            v-text="getDropdownOptionText(option, value, index)"
          />
        </select>
      </div>

      <div
        v-else
        class="product-options__values"
      >
        <template
          v-for="(value, index) of option.values"
          :key="getOptionValueKey(value, index, option)"
        >
          <selection-tab
            v-if="!isValueOnSale(value, option) && getOptionValueLabel(value)"
            :id="getOptionValueKey(value, index, option)"
            v-model="currentOptions[option.index]"
            :badge="getBadgeInfo(value)"
            class="product-options__value"
            :disabled="getValueDisabled(option.disabled[value], 'disabled')"
            :label="getOptionValueLabel(value)"
            :name="getOptionKey(option)"
            :value="value"
            :visually-disabled="getValueDisabled(option.disabled[value], 'visually-disabled')"
          >
            <template #icon>
              <component
                :is="getOptionValueIcon(value)"
                aria-hidden="true"
              />
            </template>
          </selection-tab>
        </template>
      </div>
    </fieldset>

    <select
      v-model="currentVariantId"
      class="product-options__variant-select visually-hidden"
      name="id"
      aria-hidden="true"
    >
      <option
        v-for="(variantObject, index) of product.variants"
        :key="getVariantKey(variantObject, index)"
        :value="variantObject.id"
        v-text="`${variantObject.title} - ${variantObject.id}`"
      />
    </select>
  </div>
</template>

<script>

/**
 * Vue: Product options (product-form)
 * -----------------------------------------------------------------------------
 * Product variants split into separate options and values.
 *
 * @param {Boolean} [loading] - Loading state.
 * @param {Boolean|Object} product - Canvas formatted product object.
 * @param {Object} section - Main product section settings.
 * @param {Boolean|Null|Object} variant - Canvas formatted variant object.
 *
 * @emits updateVariant - Emit new variant object.
 *
 */
import IconBellFill from '~icons/general/bell-fill.svg'
import IconBundle from '~icons/general/bundle.svg'
import IconManual from '~icons/general/manual.svg'
import IconPhone from '~icons/general/phone.svg'
import IconStarFill from '~icons/general/star-fill.svg'

import RadioInput from '~async/utils/radio-input/radio-input'
import SelectionTab from '~async/utils/selection-tab/selection-tab'

import { convertToHandle } from '~/helpers/convert'
import { emitUpdateVariant } from '~/helpers/vue'

export default {
  name: 'ProductOptions',

  components: {
    IconBellFill,
    IconBundle,
    IconManual,
    IconPhone,
    IconStarFill,
    RadioInput,
    SelectionTab,
  },

  props: {
    loading: {
      type: Boolean,
      default: false,
    },
    product: {
      type: [Boolean, Object],
      default: false,
      required: true,
    },
    section: {
      type: Object,
      default: () => ({
        displaySingleOptionNames: true,
        displaySingleValueOptions: false,
        displaySingleVariantOption: false,
        dropdownOptions: [],
        excludedInitialSelectOptions: ['length', 'size', 'waist', 'width'],
        hideGiftCardOptionNames: false,
        id: '',
        initialSelect: true,
        selectDisabledValues: true,
      }),
      required: true,
    },
    variant: {
      type: [Boolean, null, Object],
      default: false,
      required: true,
    },
  },

  emits: {
    'update-variant': emitUpdateVariant,
  },

  data() {
    return {
      contentBadge: {},
      currentVariantId: 0,
      currentOptions: [],
      labels: {
        bundle: this.$variable('settings.productOptions.bundle'),
        bundleDownload: this.$variable('settings.productOptions.bundleDownload'),
        digital: this.$variable('settings.productOptions.digital'),
        download: this.$variable('settings.productOptions.download'),
        physical: this.$variable('settings.productOptions.physical'),
      },
      options: [],
      classPrice: 'product-prices__price text-body-s-bold text-body-s-bold-desktop',
      classCompareAtPrice: 'product-prices__compare-at-price text-other-sale-s text-other-sale-s-desktop',
    }
  },

  computed: {

    /**
     * Compute if option name should be displayed.
     * @returns {Boolean}
     */
    displayOptionName() {
      if (this.product.giftCard && this.section.hideGiftCardOptionNames) {
        return false
      }

      if (
        this.section.displaySingleOptionNames ||
        (this.section.displaySingleValueOptions && this.options.length > 1)
      ) {
        return true
      }

      const multiValueOptions = this.options.filter((option) => {
        return option.values.length > 1
      })

      return multiValueOptions.length > 1
    },
  },

  watch: {

    /**
     * Watch current options to update based on option choices.
     */
    currentOptions: {
      handler() {
        this.getOptions()
        const variantTitle = this.currentOptions.join(' / ')
        const variant = this.getVariantFromProperty(variantTitle, 'title')
        this.currentVariantId = variant ? variant.id : 0
      },
      deep: true,
    },

    /**
     * Watch currentVariantId to update Canvas variant when select changes.
     */
    currentVariantId() {
      if (this.variant?.id === this.currentVariantId) {
        return
      }

      const variant = this.getVariantFromProperty(this.currentVariantId)
      this.$emit('update-variant', variant)
    },

    /**
     * Watch product to update selected variant when product data is loaded.
     * @param {Boolean|Object} newValue - New product value.
     * @param {Boolean|Object} oldValue - Old product value.
     */
    product(newValue, oldValue) {
      if (newValue?.id === oldValue?.id) {
        return
      }

      this.currentOptions = []
      this.getCurrentVariant()
    },

    /**
     * Watch variant for updates to update current options.
     * - If variant is set to false (reset form) then run mounted methods.
     */
    variant() {
      if (this.variant === false) {
        this.getOptions()
        this.getCurrentVariant()
        return
      }

      this.getCurrentOptions()
    },
  },

  mounted() {
    this.getOptions()
    this.getCurrentVariant()
  },

  methods: {

    /**
     * Compute badge data.
     * @param {String} vaue - Option value.
     * @returns {Boolean|Object}
     */
    getBadgeInfo(value) {
      const badge = {
        colorScheme: 'yellow',
        icon: null,
        text: '',
      }

      const matchingVariant = this.product.variants.find((variant) => {
        return variant.selectedOptions.some((option) => {
          return option.value === value
        })
      })

      if (!matchingVariant) {
        return false
      }

      if (matchingVariant.metafields.badgeText) {
        badge.icon = IconStarFill
        badge.text = matchingVariant.metafields.badgeText
      }

      if (!matchingVariant.available) {
        badge.colorScheme = 'grey'
        badge.icon = IconBellFill
        badge.text = this.$string('product.prices.sold_out')
      }

      return badge
    },

    /**
     * Get if single value options should be displayed.
     * @param {Object} option - Option object.
     * @returns {Boolean}
     */
    getDisplayOption(option) {
      if (this.section.displaySingleValueOptions) {
        return true
      }

      return option.values.length > 1
    },

    /**
     * Get option key.
     * @param {Object} option - Option object.
     * @returns {String}
     */
    getOptionKey(option) {
      return `${this.section.id}-${this.product.handle}-option-${option.index}-${this.getOptionHandle(option.name)}`
    },

    /**
     * Get option label.
     * @param {Object} option - Option object.
     * @returns {String}
     */
    getOptionLabel(option) {
      return option.name === 'Type' || option.name === 'Title'
        ? this.$string('product.options.label')
        : option.name
    },

    /**
     * Get option handle.
     * @param {String} name - Option name.
     * @returns {String}
     */
    getOptionHandle(optionName) {
      return convertToHandle(optionName)
    },

    /**
     * Get option value key/id.
     * @param {String} value - Value name.
     * @returns {String}
     */
    getOptionValueIcon(value) {
      let icon

      if (this.labels.bundle.includes(value)) {
        icon = IconBundle
      } else if (this.labels.bundleDownload.includes(value)) {
        icon = IconBundle
      } else if (this.labels.physical.includes(value)) {
        icon = IconManual
      } else if (this.labels.digital.includes(value)) {
        icon = IconPhone
      } else if (this.labels.download.includes(value)) {
        icon = IconPhone
      }

      return icon
    },

    /**
     * Get option value key/id.
     * @param {String} value - Value name.
     * @param {Number} index - Forloop index.
     * @param {Object} option - Option object.
     * @returns {String}
     */
    getOptionValueKey(value, index, option) {
      return `${this.getOptionKey(option)}-value-${index}-${this.getOptionHandle(value)}`
    },

    /**
     * Return if current value is on sale based on metafield data.
     * @param {String} value - Value name.
     * @param {Object} option - Option object.
     * @returns {Boolean}
     */
    isValueOnSale(value, option) {
      return option.isMetafieldOnSale[value]
    },

    /**
     * Get option value key/id.
     * @param {String} value - Value name.
     * @returns {String}
     */
    getOptionValueLabel(value) {
      if (this.product.giftCard) {
        const variant = this.getVariantFromProperty(value, 'title')
        return this.$formatMoney(variant.price.amount, this.$variable('store.moneyFormat'), true)
      }

      let label
      let formattedPrice = ''
      let formattedCompareAtPrice = ''
      let classPrice = this.classPrice

      const matchingVariant = this.product.variants.find((variant) => {
        return variant.selectedOptions.some((option) => option.value === value)
      })

      if (matchingVariant) {
        if (
          matchingVariant.compareAtPrice &&
          matchingVariant.price.amount < matchingVariant.compareAtPrice.amount
        ) {
          formattedCompareAtPrice = `<span class="${this.classCompareAtPrice}">${this.$formatMoney(matchingVariant.compareAtPrice.amount, this.$variable('store.moneyFormat'), true)}</span>&nbsp;`
          classPrice += ' product-prices__price--on-sale'
        }
        formattedPrice = `<span class="${classPrice}">${this.$formatMoney(matchingVariant.price.amount, this.$variable('store.moneyFormat'), true)}</span>`
      }

      if (this.labels.bundle.includes(value)) {
        label = this.$string('product.options.page.bundle')
      } else if (this.labels.bundleDownload.includes(value)) {
        label = this.$string('product.options.page.bundle_download')
      } else if (this.labels.physical.includes(value)) {
        label = this.$string('product.options.page.print')
      } else if (this.labels.digital.includes(value)) {
        label = this.$string('product.options.page.online')
      } else if (this.labels.download.includes(value)) {
        label = this.$string('product.options.page.download')
      } else {
        label = value
      }

      return `${label}<br>${formattedCompareAtPrice}${formattedPrice}`
    },

    /**
     * Get dropdown text.
     * @param {Object} option - Option object.
     * @param {String} value - Current option value.
     * @param {Number} index - Current option value index.
     * @returns {String}
     */
    getDropdownOptionText(option, value, index) {
      const label = option.labels[index]

      if (this.getValueDisabled(option.disabled[value], 'visually-disabled')) {
        return `${label} - ${this.$string('product.form.sold_out')}`
      }

      return label
    },

    /**
     * Get variant key.
     * @param {Object} variant - Variant object.
     * @param {Number} index - Forloop index.
     * @returns {String}
     */
    getVariantKey(variant, index) {
      return `${this.product.handle}-option-${index}-${this.getOptionHandle(variant.title)}`
    },

    /**
     * Get if value is disabled.
     * - Based on section setting and attribute.
     * @param {Boolean} available - Value availability.
     * @param {String} attribute - Seleection tab attribute.
     * @returns {Boolean}
     */
    getValueDisabled(available, attribute) {
      if (!available) {
        return false
      }

      return (
        (attribute === 'disabled' && !this.section.selectDisabledValues) ||
        (attribute === 'visually-disabled' && this.section.selectDisabledValues)
      )
    },

    /**
     * Get extended options data.
     * - Extends product data with disabled and handle data.
     * - When `loading` is `true` and `product` is `false` then this method
     *   returns placeholder options to render in the loading state.
     * @returns {Array}
     */
    getOptions() {
      if (this.loading && !this.product) {
        const values = [...new Array(3)].map(() => {
          return this.$string('accessibility.loading')
        })

        this.options = [...new Array(3)].map((_, index) => {
          return {
            disabled: true,
            handle: `storybook-${index}`,
            index,
            labels: values,
            name: this.$string('accessibility.loading'),
            values,
          }
        })

        return
      }

      if (!this.product?.options?.length) {
        this.options = []
        return
      }

      this.options = this.product.options.map((option, optionIndex) => {
        const disabled = {}
        const isMetafieldOnSale = {}

        /**
         * Compute if option is unavailable and if it should be hidden from
         * view.
         * - First find variants that match current value.
         * - Then find variants that match other currentOptions.
         * - Then test to see if all matching variants are unavailable, or if
         *   they are not saleable.
         */
        option.values.forEach((value) => {
          const filteredVariants = this.product.variants
            .filter((variant) => variant.selectedOptions[optionIndex].value === value)
            .filter((variant) => {
              return this.currentOptions.every((currentOption, currentOptionIndex) => {
                if (!currentOption || optionIndex === currentOptionIndex) {
                  return true
                }

                return variant.selectedOptions[currentOptionIndex]?.value === currentOption
              })
            })

          disabled[value] = filteredVariants
            .every((variant) => !variant.available)

          isMetafieldOnSale[value] = filteredVariants
            .every((variant) => !variant.metafields.isOnSale)
        })

        /**
         * Return extended data.
         */
        return {
          ...option,
          disabled,
          handle: this.getOptionHandle(option.name),
          isMetafieldOnSale,
        }
      })
    },

    /**
     * Get current variant on page load or product update.
     */
    getCurrentVariant() {
      if (!this.product || !this.product?.variants?.length) {
        return
      }

      if (this.$variable('storybook') && this.variant) {
        this.getCurrentOptions()
        return
      }

      /**
       * Always get current variant from URL.
       */
      const variantFromUrl = this.getVariantFromUrl()

      if (variantFromUrl) {
        this.currentVariantId = variantFromUrl.id
        return
      }

      /**
       * Do not set current variant if disabled in settings.
       */
      if (!this.section.initialSelect) {
        this.getCurrentOptions()
        return
      }

      /**
       * Pick the first in stock variant.
       * - Ensure variant isOnSale is not set to false.
       * - If no variants are in stock then use first variant.
       */
      const inStock = this.product.variants.filter((variant) => {
        return variant.available && variant.metafields?.isOnSale !== false
      })

      const currentVariant = inStock.length ? inStock[0] : this.product.variants[0]

      /**
       * Only set current variant if none of the variant options are excluded
       * from being initially set.
       * - Except where excluded option has only a single value, otherwise if
       *   single value options are not shown the variant will not be
       *   selectable by the user.
       */
      const currentOptions = currentVariant.selectedOptions.map((selectedOption) => {
        const optionHandle = this.getOptionHandle(selectedOption.name)
        const matchedOption = this.options.find((option) => option.handle === optionHandle)

        const excludedOption =
          this.section.excludedInitialSelectOptions.includes(optionHandle) &&
          matchedOption.values.length > 1

        return excludedOption ? '' : selectedOption.value
      })

      const filteredCurrentOptions = currentOptions.filter(Boolean)

      if (filteredCurrentOptions.length !== currentVariant.selectedOptions.length) {
        this.currentOptions = currentOptions
        return
      }

      this.currentVariantId = currentVariant.id
      this.getCurrentOptions()
    },

    /**
     * Get initial variant from URL parameter.
     * @returns {Object}
     */
    getVariantFromUrl() {
      const params = new URLSearchParams(document.location.search.substring(1))
      const variantId = Number(params.get('variant'))
      return this.getVariantFromProperty(variantId)
    },

    /**
     * Get variant object from variant property.
     * @param {String} value - Value to match.
     * @param {String} property - Property to find by.
     * @returns {Object|Null}
     */
    getVariantFromProperty(value, property = 'id') {
      const matchingVariant = this.product.variants?.find((variant) => {
        return variant[property] === value
      })

      return matchingVariant ? matchingVariant : null
    },

    /**
     * Get current options.
     * - Boolean variant is used to reset current options.
     * - Null variant just means selected options hasn't found a variant
     *   and shouldn't reset the current options.
     */
    getCurrentOptions() {
      if (!this.variant) {
        if (this.variant === null) {
          return
        }

        this.currentOptions = Array.from(Array(this.product?.options?.length)).map(() => '')
        return
      }

      this.variant.selectedOptions.forEach((selectedOption, index) => {
        this.currentOptions[index] = selectedOption.value
      })
    },
  },
}
</script>

<style lang="scss">
@import './product-options';
</style>
